
// Brand-consistent gradients, picked deterministically so a given card always looks the same.
const PLACEHOLDER_THEMES = [
  ['#26c6a3', '#0e8a9c'],   // mint -> teal, the primary brand pair
  ['#0e8a9c', '#0c2b2f'],   // teal -> deep sea
  ['#2ad0b0', '#127f96'],
  ['#f7c948', '#0e8a9c'],   // warm sand -> teal
  ['#0f9c8a', '#083b45'],
  ['#35d1a4', '#0b6e86'],
];

const titleise = (kw) =>
  String(kw || '').split(',')[0].replace(/[-_]+/g, ' ').trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * A branded placeholder, not a photograph.
 *
 * The site previously pulled random stock from an external service, which was unreliable and
 * regularly showed the wrong place entirely — a Punta Cana article illustrated with Thailand.
 * Until real photography is uploaded, an honest placeholder beats a misleading picture. Returns
 * an inline SVG data URI, so it needs no network and can never fail to load.
 */
export const pic = (kw, lock, w, h) => {
  const width = w || 800;
  const height = h || 600;
  const label = titleise(kw);
  const [from, to] = PLACEHOLDER_THEMES[Math.abs(Number(lock) || 0) % PLACEHOLDER_THEMES.length];
  const seed = Math.abs(Number(lock) || 0);
  const cx = 20 + (seed * 13) % 60;
  const cy = 24 + (seed * 7) % 46;
  // A quiet bottom-left caption, not a headline — it must identify a card without
  // competing with a title laid over the same image in hero positions.
  const fs = Math.max(13, Math.min(24, Math.round(Math.min(width, height) / 22)));
  const pad = Math.round(fs * 1.2);

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
      `<defs>` +
        `<linearGradient id="g" x1="0" y1="0" x2="1" y2="1">` +
          `<stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/>` +
        `</linearGradient>` +
        `<radialGradient id="h" cx="${cx}%" cy="${cy}%" r="62%">` +
          `<stop offset="0" stop-color="#ffffff" stop-opacity="0.30"/>` +
          `<stop offset="1" stop-color="#ffffff" stop-opacity="0"/>` +
        `</radialGradient>` +
      `</defs>` +
      `<rect width="${width}" height="${height}" fill="url(#g)"/>` +
      `<rect width="${width}" height="${height}" fill="url(#h)"/>` +
      // a low horizon line, enough to read as landscape without pretending to be one
      `<path d="M0 ${height * 0.74} Q ${width * 0.3} ${height * 0.68} ${width * 0.55} ${height * 0.74} ` +
        `T ${width} ${height * 0.72} L ${width} ${height} L 0 ${height} Z" fill="#ffffff" opacity="0.10"/>` +
      `<text x="${pad}" y="${height - pad}" text-anchor="start" ` +
        `font-family="system-ui,-apple-system,Segoe UI,Helvetica,Arial,sans-serif" ` +
        `font-size="${fs}" font-weight="600" letter-spacing="0.02em" ` +
        `fill="#ffffff" opacity="0.78">${label}</text>` +
    `</svg>`;

  // encodeURIComponent leaves parentheses and apostrophes intact, and the SVG contains
  // fill="url(#g)" — an unencoded ")" terminates a CSS url(...) early and the image
  // silently fails to paint. Encode them explicitly.
  const encoded = encodeURIComponent(svg).replace(/\(/g, '%28').replace(/\)/g, '%29').replace(/'/g, '%27');
  return 'data:image/svg+xml;charset=utf-8,' + encoded;
};






export const PERKS = [
  { n:'1', title:'Real human experts', text:'Talk to a person who’s been there — not a chatbot or a faceless booking site.' },
  { n:'2', title:'One price, all-in', text:'Flights, hotel, transfers and tours bundled with no surprise fees at checkout.' },
  { n:'3', title:'24/7 on-trip support', text:'A flight delay or change of plan? We’re a WhatsApp message away, any hour.' },
  { n:'4', title:'Transparent quotes', text:'The price we send is the price you pay — every quote itemised in writing before you commit.' },
];

export const FILTERS = ['All','Beach & Resort','Honeymoon','Culture & History','Family','Adventure'];
export const INCLUDED = ['Return flights','Airport transfers','All-inclusive resort','Daily breakfast+','Local support line','1 signature excursion'];


const C = (name, kw, lock, tag) => ({ key:name.toLowerCase().replace(/[^a-z0-9]+/g,'-'), name, tag, img:pic(kw,lock) });

export const REGIONS = [
  // Aligned to the countries the agency actually sells (operator's list, 2026-08-14).
  { key:'caribbean', name:'Caribbean', blurb:'Turquoise water, white sand and all-inclusive ease.', img:pic('caribbean,beach,turquoise',101), countries:[C('Dominican Republic','puntacana,beach,palm',11,'Beach & Resort'),C('Cuba','varadero,beach,cuba',12,'Culture & Beach'),C('Jamaica','jamaica,beach,tropical',102,'Beach & Music'),C('Aruba','aruba,beach,caribbean',103,'Beach & Resort'),C('The Bahamas','bahamas,beach,island',104,'Island Hopping'),C('Puerto Rico','sanjuan,beach,colonial',105,'City & Beach')] },
  { key:'europe', name:'Europe', blurb:'Old cities, coastlines and centuries of culture.', img:pic('europe,city,architecture',106), countries:[C('Italy','italy,amalfi,coast',107,'Culture & Coast'),C('Spain','tenerife,beach,canary',108,'Islands & Beach'),C('Greece','greece,santorini,island',109,'Islands'),C('France','france,riviera,nice',110,'Romance'),C('Monaco','monaco,montecarlo,harbor',111,'Luxury'),C('Montenegro','kotor,budva,adriatic',149,'Coast & Heritage')] },
  { key:'asia', name:'Asia', blurb:'Temples, street food and impossible beaches.', img:pic('asia,temple,tropical',112), countries:[C('Armenia','armenia,ararat,monastery',153,'Culture & History'),C('Thailand','thailand,beach,island',114,'Beach & Culture'),C('Indonesia','bali,rice,beach',116,'Islands'),C('Vietnam','danang,hoian,beach',117,'Beach & Heritage'),C('Malaysia','langkawi,beach,island',150,'Islands'),C('Philippines','palawan,elnido,lagoon',151,'Islands & Diving'),C('Maldives','maldives,overwater,lagoon',152,'Honeymoon')] },
  { key:'middle-east', name:'Middle East', blurb:'Desert luxury, souks and warm winter sun.', img:pic('dubai,desert,skyline',118), countries:[C('UAE','dubai,skyline,luxury',119,'Luxury & City'),C('Egypt','pyramids,giza,egypt',13,'History & Cruise')] },
  { key:'africa', name:'Africa', blurb:'Medinas, mountains and desert light.', img:pic('morocco,desert,medina',122), countries:[C('Morocco','morocco,marrakech,riad',125,'Culture & Desert')] },
  { key:'north-america', name:'North America', blurb:'Caribbean sand on the Mexican coast.', img:pic('cancun,beach,caribbean',126), countries:[C('Mexico','cancun,beach,caribbean',14,'Beach & Ruins')] },
  { key:'south-america', name:'South America', blurb:'Carnival energy, Andes peaks and Atlantic beaches.', img:pic('brazil,rio,mountain',129), countries:[C('Brazil','rio,beach,carnival',130,'Beach & Carnival'),C('Peru','machupicchu,peru,andes',131,'History & Trek'),C('Argentina','buenosaires,tango,city',132,'City & Culture')] },
];


// Each event carries its own page, so `key` is the URL and `body` is what that page says.
// Tickets for most of these go on sale six to twelve months ahead, which is the single most
// useful thing we can tell someone — so every entry leads with when to book, not just when it is.


export const INSPIRATION = ['☀ Warm All Year','🏝 White Sand Beaches','🍷 Wine Lovers','🏔 Mountains','🌋 Volcanoes','🐠 Diving','🦜 Wildlife','❤️ Romance','👨‍👩‍👧 Family','💎 Luxury','🚢 Cruise','🌅 Sunset','🌴 Tropical'];

export const CONCIERGE = [
  ['Private Transfers','Door-to-door cars with vetted local drivers.','transfer,car,luxury',180],
  ['Restaurant Reservations','Tables at the places that are "fully booked".','restaurant,dining,table',181],
  ['VIP Airport Service','Fast-track, lounge access and escorted arrivals.','airport,lounge,vip',182],
  ['Yacht Charters','Half-day sails to week-long private charters.','yacht,sailing,sea',183],
  ['Helicopter Tours','See the coastline the way it deserves to be seen.','helicopter,aerial,coast',184],
  ['Private Guides','Historians, chefs and divers — not scripted tours.','guide,tour,local',185],
  ['Luxury Villas','Staffed villas with pools, chefs and privacy.','luxury,villa,pool',186],
  ['Surprise Celebrations','Birthdays and anniversaries, quietly arranged.','celebration,champagne,sunset',187],
  ['Proposal Planning','The setting, the photographer, the perfect moment.','proposal,beach,romance',188],
  ['Wedding Ceremony','Beach or chapel, paperwork handled end to end.','wedding,beach,ceremony',189],
  ['Honeymoon Planning','Two weeks that feel like a lifetime highlight.','honeymoon,overwater,villa',190],
  ['Corporate Retreats','Offsites your team will actually talk about.','retreat,resort,meeting',191],
].map(([name, desc, kw, lock]) => ({ name, desc, img: pic(kw, lock) }));

export const TRAVELINFO = [
  ['Before You Book','What we need from you, deposits and payment plans.'],
  ['Passports & Visas','Validity rules, e-visas and entry requirements by country.'],
  ['Travel Insurance','What a good policy covers and why we insist on one.'],
  ['Health & Vaccinations','Region-by-region guidance and pharmacy tips.'],
  ['Baggage & Airlines','Allowances, carry-on rules and seat selection.'],
  ['Airport & Transfers','How arrivals work and where to find your driver.'],
  ['Money & Tipping','Cards, cash, and what to tip where.'],
  ['Safety & Support','Our 24/7 line and what to do if plans change.'],
].map(([title, text]) => ({ title, text }));

export const FAQS = [
  ['How do I book a trip?','Message us on WhatsApp or send the contact form. We reply with 2–3 tailored options within 24 hours, then hold your booking with a deposit.'],
  ['How much deposit do you take?','Deposit and balance terms depend on the airline and hotel — we confirm the exact amounts and dates in writing before you pay anything.'],
  ['Are flights included in the prices?','Package prices include return flights, transfers and accommodation unless stated otherwise. Land-only pricing is available too.'],
  ['Can I change or cancel my booking?','Yes — terms depend on the airline and hotel. We always tell you the exact rules in writing before you pay.'],
  ['Do you arrange visas?','We confirm the requirements for your nationality and guide you through e-visas, but the application itself stays in your name.'],
  ['What if something goes wrong while I travel?','Our 24/7 support line is on your itinerary. A real person answers, in your language, wherever you are.'],
  ['Do you handle group and corporate trips?','Yes — weddings, family groups, incentives and corporate retreats are a large part of what we do.'],
  ['Is travel insurance required?','It is not legally required for most destinations, but we strongly recommend it and can arrange a policy for you.'],
].map(([q, a]) => ({ q, a }));

export const COUNTRY_TABS = [['overview','Overview'],['best-time','Best Time to Visit'],['resorts','Resorts'],['hotels','Hotels'],['excursions','Excursions'],['currency','Currency'],['weather','Weather'],['visa','Visa Requirements'],['tips','Travel Tips'],['faq','FAQ']];




